import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import { isApiError } from '@pc/api/client';
import { useMe, loginUrl, type Identity } from '../auth/useMe';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { KnownPerson } from '../components/accounts/KnownPerson';
import { OwnerPicker } from '../components/accounts/OwnerPicker';
import { Badge, Card, Empty, Facts, Id, Loading, Person } from '../components/ui';
import { shortJobId, toneForJobState, type JobPage } from '@pc/lib/jobs';
import { formatTimestamp } from '@pc/lib/time';

export const Route = createFileRoute('/')({
  component: Home,
});

/**
 * The home route: find an account, see what changed, then confirm your access.
 *
 * The API health panel used to render here as well, and that was the wrong
 * place for it by exactly one level of nesting: `<SessionGate>` in
 * `routes/__root.tsx` replaces the whole outlet when `/api/auth/me` fails, so
 * this route never rendered in the one situation the panel existed for. It now
 * lives in `components/ApiHealth.tsx` and is mounted beside the gate rather
 * than beneath it, which is why there is no second copy of it here — one
 * liveness reading, above every screen, or an operator has to guess which of
 * the two they are looking at. Find comes first because it is why an operator
 * came; recent jobs follow because they are what changed while they were away;
 * identity is last because the header already shows it and nobody opens this
 * page to read it.
 */
function Home() {
  return (
    <section>
      <h1>Platform Control</h1>
      <FindAccount />
      <RecentJobs />
      <YourAccess />
    </section>
  );
}

/**
 * Only the access card depends on the session read, so only it waits on one.
 *
 * `SessionGate` in `routes/__root.tsx` already replaces the outlet when
 * `/api/auth/me` fails, so the branches below are the belt to its braces —
 * this route does not render at all in the case they describe. They are kept
 * because a component that reads `me` should say what it does without one,
 * not because this screen is expected to reach them.
 */
function YourAccess() {
  const me = useMe();

  if (me.isPending) return <p>Checking your session…</p>;

  if (me.isError) {
    // 401 is not a failure to report; it is simply "not logged in yet".
    if (isApiError(me.error) && me.error.kind === 'unauthenticated') {
      return <a href={loginUrl()}>Log in with Okta</a>;
    }
    return <ApiErrorNotice error={me.error} onRetry={() => void me.refetch()} />;
  }

  return <Whoami identity={me.data} />;
}

function Whoami({ identity }: { identity: Identity }) {
  // Only oktaId, level, orgLevel, levelSource and groups are guaranteed by the
  // contract. Everything else is optional, so fall back rather than rendering
  // a bare `undefined`.
  const name = identity.displayName ?? identity.email ?? identity.oktaId;

  return (
    <Card title="Your access">
      <Facts
        items={[
          [
            'Name',
            <>
              {name}
              {identity.email && identity.displayName ? ` (${identity.email})` : null}
            </>,
          ],
          ['Level', `${identity.level} — ${describeLevelSource(identity)}`],
          ['Groups', identity.groups.length > 0 ? identity.groups.join(', ') : 'none'],
        ]}
      />

      {identity.levelSource === 'unresolved' && (
        <p role="alert">
          Your group membership did not resolve to an access level. This is a configuration
          problem with the account, not a decision about you — most actions will be refused
          until it is fixed.
        </p>
      )}

      {identity.degraded && (
        <p role="status">
          Your level could not be freshly confirmed against Okta, so what you see may be stale.
          This is not a change to your access.
        </p>
      )}
    </Card>
  );
}

function FindAccount() {
  const navigate = useNavigate();

  return (
    <Card title="Find an account">
      {/*
        * The label is real but not drawn: the card is titled "Find an account"
        * directly above a single field, so a visible "Account" beside it says
        * the same thing twice. Hiding it visually rather than dropping it
        * keeps the field named for anyone not reading the card title — and
        * `OwnerPicker` takes an `id` but no label of its own, so this is where
        * that name has to come from.
        */}
      <label className="visually-hidden" htmlFor="account">
        Find an account
      </label>
      <OwnerPicker
        id="account"
        value=""
        onChange={(discordID) => {
          if (discordID !== '') void navigate({ to: '/accounts', search: { discordID } });
        }}
      />
      <p className="hint">Whichever you have — selecting one opens it.</p>
    </Card>
  );
}

function RecentJobs() {
  const jobs = useQuery({
    queryKey: ['jobs', 'recent'],
    queryFn: async (): Promise<JobPage> => {
      const { data } = await api.GET('/api/jobs', { params: { query: { limit: 5 } } });
      return data as JobPage;
    },
  });

  return (
    <Card title="Recent jobs">
      {jobs.isPending && <Loading what="jobs" />}
      {jobs.isError && <ApiErrorNotice error={jobs.error} onRetry={() => void jobs.refetch()} />}
      {jobs.isSuccess && jobs.data.records.length === 0 && <Empty>No jobs have run yet.</Empty>}
      {jobs.isSuccess && jobs.data.records.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Kind</th>
                <th>State</th>
                <th>When</th>
                <th>Job</th>
                <th>Subject</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              {jobs.data.records.map((job) => (
                <tr key={job.jobId}>
                  <td>{job.kind}</td>
                  <td>
                    <Badge tone={toneForJobState(job.state)}>{job.state}</Badge>
                  </td>
                  {/*
                    * "When", not "Started": `startedAt` is null while a job is
                    * queued — accepted, nothing has picked it up — and the
                    * creation time standing in under a column headed Started
                    * would say a queued job had started. The useful fact for a
                    * queued job is how long it has been waiting, so it is the
                    * column that gives way rather than the cell.
                    */}
                  <td>{formatTimestamp(job.startedAt ?? job.createdAt)}</td>
                  <td>
                    <Link to="/jobs" search={{ jobId: job.jobId }} title="Open this job">
                      <span title={job.jobId}>
                        <Id>{shortJobId(job.jobId)}</Id>
                      </span>
                    </Link>
                  </td>
                  {/*
                    * Both the subject and the actor are on every job record —
                    * "what changed while I was away" could not previously say
                    * what changed or who did it. Rendered exactly as
                    * `JobsBrowser` renders the same two fields off the same
                    * record shape: `KnownPerson` for the subject (a member,
                    * named only from accounts this client already has
                    * cached), `Person` for the actor (the job's own persisted
                    * snapshot of whoever started it).
                    */}
                  <td>
                    <KnownPerson discordID={job.subjectDiscordID} linkTitle="Open this account" />
                  </td>
                  <td>
                    <Person person={job.actor} compact showId={false} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p>
        <Link to="/jobs">All jobs</Link>
      </p>
    </Card>
  );
}

function describeLevelSource(identity: Identity): string {
  switch (identity.levelSource) {
    case 'inherited':
      // platformctl-operators takes the person's own org level.
      return `inherited from your org level (${identity.orgLevel})`;
    case 'granted':
      // A platformctl-l1…l5 group grants a fixed level regardless of org level.
      return `granted directly by group (org level ${identity.orgLevel})`;
    case 'none':
      return 'no operator grant';
    case 'unresolved':
      return 'could not be resolved';
  }
}
